import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, ChevronDown, ChevronRight, Briefcase, MapPin, Phone,
  ThumbsUp, ThumbsDown, User, Search, Building2
} from 'lucide-react';
import { format } from 'date-fns';

interface GroupedData {
  campaignId: string;
  campaignTitle: string;
  companyName: string;
  country: string;
  jobs: {
    jobId: string;
    role: string;
    applicants: any[];
  }[];
}

export default function Applicants() {
  const { activeUserId } = useAuth();
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState<GroupedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (activeUserId) fetchApplicants();
  }, [activeUserId]);

  const fetchApplicants = async () => {
    try {
      // Get all campaigns for this recruiter
      const { data: campaigns, error: campErr } = await supabase
        .from('campaigns')
        .select('id, title, company_name, country')
        .eq('recruiter_id', activeUserId!);
      if (campErr) throw campErr;
      if (!campaigns || campaigns.length === 0) { setGrouped([]); return; }

      const result: GroupedData[] = [];

      for (const camp of campaigns) {
        // Get jobs for this campaign
        const { data: jobs } = await supabase
          .from('jobs')
          .select('id, role')
          .eq('campaign_id', camp.id);

        if (!jobs || jobs.length === 0) continue;

        const jobData: GroupedData['jobs'] = [];

        for (const job of jobs) {
          const { data: apps } = await supabase
            .from('applications')
            .select(`
              *,
              candidate:candidates(*)
            `)
            .eq('job_id', job.id)
            .order('created_at', { ascending: false });

          if (apps && apps.length > 0) {
            jobData.push({ jobId: job.id, role: job.role, applicants: apps });
          }
        }

        if (jobData.length > 0) {
          result.push({
            campaignId: camp.id,
            campaignTitle: camp.title,
            companyName: camp.company_name,
            country: camp.country,
            jobs: jobData,
          });
        }
      }

      setGrouped(result);
      // Auto-expand first campaign
      if (result.length > 0) {
        setExpandedCampaigns(new Set([result[0].campaignId]));
        if (result[0].jobs.length > 0) {
          setExpandedJobs(new Set([result[0].jobs[0].jobId]));
        }
      }
    } catch (err) {
      console.error('Error fetching applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (appId: string, candidateId: string, newStage: 'shortlisted' | 'rejected', jobRole: string) => {
    try {
      await supabase.from('applications').update({ stage: newStage }).eq('id', appId);

      const notif = newStage === 'shortlisted'
        ? { title: 'Good News! 🎉', body: `You've been shortlisted for ${jobRole}!`, type: 'application_shortlisted' }
        : { title: 'Application Update', body: `Your application for ${jobRole} was not selected.`, type: 'application_rejected' };

      await supabase.from('notifications').insert({
        user_id: candidateId,
        type: notif.type,
        title: notif.title,
        body: notif.body,
        data: JSON.stringify({ applicationId: appId }),
      });

      fetchApplicants();
    } catch (err) {
      console.error('Error updating applicant:', err);
    }
  };

  const toggleCampaign = (id: string) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleJob = (id: string) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalApplicants = grouped.reduce((acc, g) => acc + g.jobs.reduce((a, j) => a + j.applicants.length, 0), 0);

  const stageColors: Record<string, string> = {
    applied: 'bg-blue-50 text-blue-700',
    viewed: 'bg-slate-100 text-slate-600',
    shortlisted: 'bg-emerald-50 text-emerald-700',
    interview_scheduled: 'bg-amber-50 text-amber-700',
    interviewed: 'bg-violet-50 text-violet-600',
    selected: 'bg-emerald-50 text-emerald-700',
    confirmed: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-600',
    pending: 'bg-slate-100 text-slate-500',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Candidates</h1>
          <p className="text-slate-400 mt-1 text-[15px]">{totalApplicants} candidates across {grouped.length} campaigns</p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search candidates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 transition-all"
          />
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/60 shadow-sm">
          <Users size={40} className="mx-auto text-slate-200 mb-3" />
          <p className="text-[15px] font-semibold text-slate-700">No applicants yet</p>
          <p className="text-[13px] text-slate-400 mt-1">Candidates who apply to your campaigns will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(group => (
            <div key={group.campaignId} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
              {/* Campaign Header */}
              <button
                onClick={() => toggleCampaign(group.campaignId)}
                className="w-full flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                  <Briefcase size={18} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 truncate">{group.campaignTitle}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Building2 size={12} /> {group.companyName}</span>
                    <span className="flex items-center gap-1"><MapPin size={12} /> {group.country}</span>
                  </p>
                </div>
                <span className="text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {group.jobs.reduce((a, j) => a + j.applicants.length, 0)} applicants
                </span>
                <ChevronDown size={20} className={`text-gray-400 transition-transform ${expandedCampaigns.has(group.campaignId) ? '' : '-rotate-90'}`} />
              </button>

              {/* Jobs inside campaign */}
              {expandedCampaigns.has(group.campaignId) && (
                <div className="border-t border-gray-100">
                  {group.jobs.map(job => (
                    <div key={job.jobId}>
                      <button
                        onClick={() => toggleJob(job.jobId)}
                        className="w-full flex items-center gap-3 py-3 px-8 hover:bg-gray-50 transition-colors text-left border-b border-gray-50"
                      >
                        <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedJobs.has(job.jobId) ? 'rotate-90' : ''}`} />
                        <span className="font-semibold text-gray-700">{job.role}</span>
                        <span className="text-xs text-gray-400 font-medium">{job.applicants.length} candidates</span>
                      </button>

                      {expandedJobs.has(job.jobId) && (
                        <div className="divide-y divide-gray-50">
                          {job.applicants
                            .filter(app => !search || (app.candidate?.name || '').toLowerCase().includes(search.toLowerCase()))
                            .map(app => (
                              <div key={app.id} className="flex items-center gap-4 py-4 px-10 hover:bg-blue-50/30 transition-colors group">
                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                                  {app.candidate?.photo_url
                                    ? <img src={app.candidate.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                                    : <User size={18} className="text-gray-400" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-gray-900 truncate">{app.candidate?.name || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500 flex items-center gap-3">
                                    {app.candidate?.phone && <span className="flex items-center gap-1"><Phone size={10} /> {app.candidate.phone}</span>}
                                    {app.candidate?.city && <span className="flex items-center gap-1"><MapPin size={10} /> {app.candidate.city}</span>}
                                  </p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${stageColors[app.stage] || stageColors.applied}`}>
                                  {(app.stage || 'applied').replace('_', ' ')}
                                </span>
                                {app.stage === 'applied' && (
                                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => handleQuickAction(app.id, app.candidate?.id, 'rejected', job.role)}
                                      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                                      title="Reject"
                                    >
                                      <ThumbsDown size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleQuickAction(app.id, app.candidate?.id, 'shortlisted', job.role)}
                                      className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                                      title="Shortlist"
                                    >
                                      <ThumbsUp size={16} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
